import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Tag,
  Trash2,
} from "lucide-react";

import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { AddLeadPanel } from "../components/AddLeadPanel";
import { LeadsCard } from "../components/LeadsCard";
import { Timeline } from "../components/Timeline";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton, StageBadge } from "../components/ui";
import { formatDate, formatDateTime, formatMoney, plural, sumMoney } from "../lib/format";

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [addingLead, setAddingLead] = useState(false);
  const label = user?.account?.primary_object_label ?? "Contact";

  const query = useQuery({
    queryKey: ["contact", id],
    queryFn: ({ signal }) => api.contact(id!, signal),
    enabled: Boolean(id),
  });

  // Fetched here rather than inside LeadsCard so the sidebar total and the list
  // are computed from one request. Two components fetching the same rows is the
  // N+1 of React Query.
  const leadsQuery = useQuery({
    queryKey: ["leads", id],
    queryFn: ({ signal }) => api.leads({}, signal, { contactId: id! }),
    enabled: Boolean(id),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteContact(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["usage"] });
      navigate("/customers/contacts");
    },
  });

  if (query.isPending) {
    // No heading while loading: a heading that says "Loading…" is announced as
    // the page title by a screen reader, then flickers to the real name.
    return (
      <>
        <BackLink />
        <div className="space-y-6" aria-busy="true">
          <Card>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="mt-4 h-4 w-72" />
            <Skeleton className="mt-2 h-4 w-56" />
          </Card>
        </div>
      </>
    );
  }

  if (query.isError || !query.data) {
    return (
      <>
        <BackLink />
        <PageHeader title="Not found" />
        <Card>
          <EmptyState
            title="That record is not available"
            body="It may have been deleted, or it belongs to another account."
            action={
              <Link to="/customers/contacts" className="text-sm font-medium underline">
                Back to {plural(label).toLowerCase()}
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  const contact = query.data;
  const owing = Number(contact.outstanding_balance.amount ?? 0);

  // Only open enquiries count. A dropped or converted one is history, and adding
  // it to "what this person is discussing" would double-count somebody who
  // enquired twice about the same thing.
  const openLeads = (leadsQuery.data?.results ?? []).filter((lead) => lead.is_open);
  const leadValue = sumMoney(openLeads.map((lead) => lead.quoted_price));
  const biggestLead = openLeads.reduce<(typeof openLeads)[number] | null>(
    (best, lead) =>
      !best || Number(lead.quoted_price?.amount ?? 0) > Number(best.quoted_price?.amount ?? 0)
        ? lead
        : best,
    null,
  );
  const tags = (contact.tags as string[] | undefined) ?? [];
  const canDelete = (user?.permissions ?? []).includes("contacts.write");

  return (
    <>
      <BackLink />
      <PageHeader
        title={contact.display_name}
        subtitle={[contact.company_name, contact.city].filter(Boolean).join(" · ") || undefined}
        actions={
          <>
            <Link
              to={`/customers/contacts/${contact.id}/edit`}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
            >
              <Pencil size={14} /> Edit
            </Link>
            {canDelete ? (
              <Button onClick={() => setConfirmingDelete(true)}>
                <Trash2 size={14} /> Delete
              </Button>
            ) : null}
          </>
        }
      />

      {confirmingDelete ? (
        <Card className="mb-6 border-rose-200 bg-rose-50/50">
          <p className="text-sm font-medium text-rose-900">
            Delete {contact.display_name}?
          </p>
          <p className="mt-1 text-sm text-rose-800">
            This removes them from your lists but keeps the record and its history
            for 30 days. You can restore them from the Bin.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="bg-rose-700 hover:bg-rose-800"
            >
              {remove.isPending ? "Deleting…" : "Yes, delete"}
            </Button>
            <Button onClick={() => setConfirmingDelete(false)}>Cancel</Button>
          </div>
          {remove.isError ? (
            <p role="alert" className="mt-2 text-sm text-rose-700">
              Could not delete. Try again.
            </p>
          ) : null}
        </Card>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold">Details</h2>
              <StageBadge stage={contact.lifecycle_stage} />
              {contact.source ? <Badge>{contact.source.replace(/_/g, " ")}</Badge> : null}
            </div>

            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field label="Email">
                {contact.email ? (
                  <a
                    href={`mailto:${contact.email}`}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-900 underline"
                  >
                    <Mail size={14} className="text-slate-400" />
                    {contact.email}
                  </a>
                ) : (
                  "—"
                )}
              </Field>
              <Field label="Phone">
                {contact.phone ? (
                  <a
                    href={`tel:${contact.phone}`}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-900 underline"
                  >
                    <Phone size={14} className="text-slate-400" />
                    {contact.phone}
                  </a>
                ) : (
                  "—"
                )}
              </Field>
              <Field label="WhatsApp">{contact.whatsapp ?? "—"}</Field>
              <Field label="City">
                <span className="inline-flex items-center gap-1.5">
                  {contact.city ? <MapPin size={14} className="text-slate-400" /> : null}
                  {contact.city ?? "—"}
                </span>
              </Field>
              <Field label="Date of birth">{formatDate(contact.dob)}</Field>
              <Field label="Marketing opt-in">{contact.marketing_opt_in ? "Yes" : "No"}</Field>
            </dl>

            {tags.length > 0 ? (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                  <Tag size={12} /> Tags
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/customers/contacts?tags=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}

            {contact.notes ? (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                  Notes
                </p>
                <p className="whitespace-pre-wrap text-sm text-slate-700">{contact.notes}</p>
              </div>
            ) : null}
          </Card>

          {addingLead ? (
            <AddLeadPanel contactId={contact.id} onDone={() => setAddingLead(false)} />
          ) : null}

          <LeadsCard
            leads={leadsQuery.data?.results ?? []}
            pending={leadsQuery.isPending}
            error={leadsQuery.isError ? leadsQuery.error : null}
            onAdd={() => setAddingLead((v) => !v)}
            adding={addingLead}
          />

          <Timeline contactId={contact.id} />
        </div>

        <div className="space-y-6">
          {/* What this person is discussing is worth more attention than what
              they owe -- which stays at zero until Billing lands in phase 3. So
              the enquiry value leads, and the dates sit at the bottom. */}
          <Card
            className={leadValue ? "border-slate-300" : undefined}
          >
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Lead value
            </p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
              {leadValue ? formatMoney(leadValue) : "—"}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {openLeads.length === 0
                ? "No open enquiries."
                : `${openLeads.length} open ${openLeads.length === 1 ? "enquiry" : "enquiries"}` +
                  (leadValue ? " quoted" : ", none priced yet")}
            </p>
            {biggestLead?.product_name ? (
              <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
                Biggest: {biggestLead.product_name}
                {biggestLead.quoted_price
                  ? ` · ${formatMoney(biggestLead.quoted_price)}`
                  : ""}
              </p>
            ) : null}
          </Card>

          <Card>
            <h2 className="mb-4 text-sm font-semibold">Balance</h2>
            <p className="text-2xl font-semibold tracking-tight text-slate-900">
              {formatMoney({
                amount: owing === 0 ? "0" : contact.outstanding_balance.amount,
                currency: contact.outstanding_balance.currency,
              })}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {owing === 0 ? "Nothing outstanding." : "Outstanding balance."}
            </p>
            <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
              Resolved from the invoice ledger at read time, never stored on the
              contact. Billing lands in phase 3.
            </p>
          </Card>

          <Card>
            <h2 className="mb-4 text-sm font-semibold">Record</h2>
            <dl className="space-y-3 text-sm">
              <Row label="Created" value={formatDateTime(contact.created_at)} />
              <Row label="First contacted" value={formatDateTime(contact.first_contacted_at)} />
              <Row label="Last contacted" value={formatDateTime(contact.last_contacted_at)} />
              <Row label="Next follow-up" value={formatDateTime(contact.next_follow_up_at)} />
            </dl>
          </Card>
        </div>
      </div>
    </>
  );
}

function BackLink() {
  const { user } = useAuth();
  const label = user?.account?.primary_object_label ?? "Contact";
  return (
    <Link
      to="/customers/contacts"
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> All {plural(label)}
    </Link>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-800">{children}</dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-800">{value}</dd>
    </div>
  );
}